import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import useScreenDimensions from '@/hooks/useScreenDimensions';
import type { ChatHistoryType } from '@/types/converse/chatHistory';
import { Box, Paper, Typography } from '@mui/material';
import React from 'react';
import type { TooltipProps } from 'recharts';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function CustomTooltip({
  active,
  payload,
  label,
}: TooltipProps<number, string>): React.ReactNode {
  if (active && payload?.length) {
    return (
      <div className="bg-gray-800 text-white p-3 rounded shadow-lg border border-gray-700">
        <p className="font-medium">{`${label}`}</p>
        <p className="text-blue-300">{`${payload[0].value} calls`}</p>
      </div>
    );
  }
  return null;
}

interface CallsChartProps {
  callsHistory: ChatHistoryType[];
}

const groupCalls = (
  callConversations: ChatHistoryType[]
): { date: string; calls: number }[] => {
  let groupedCalls = callConversations.reduce<Record<string, number>>(
    (acc, conv) => {
      const date = new Date(conv.updatedAt ?? Date.now()).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    },
    {}
  );

  // Get the dates with data
  const dates = Object.keys(groupedCalls).map((date) => new Date(date));
  const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

  // Calculate padding days
  const existingDays =
    Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)) +
    1;
  const remainingDays = 7 - existingDays;
  const beforePadding = Math.ceil(remainingDays / 2);
  const afterPadding = Math.floor(remainingDays / 2);

  // Add padding days
  for (let i = 1; i <= beforePadding; i++) {
    const date = new Date(minDate);
    date.setDate(date.getDate() - i);
    groupedCalls[date.toLocaleDateString()] = 0;
  }

  for (let i = 1; i <= afterPadding; i++) {
    const date = new Date(maxDate);
    date.setDate(date.getDate() + i);
    groupedCalls[date.toLocaleDateString()] = 0;
  }
  return Object.entries(groupedCalls)
    .map(([date, calls]) => ({ date, calls }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

function CallsChart({ callsHistory }: CallsChartProps): React.ReactNode {
  const { height: screenHeight, width } = useScreenDimensions();
  const callsData: { date: string; calls: number }[] = groupCalls(callsHistory);
  const currentTheme = useAlchemystStoreForAi((store) => store.theme);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: '#1e1e1e',
        height: '42vh',
        color: 'white',
        width: '100%',
        border: '0.2rem solid transparent',
        background:
          currentTheme === 'dark'
            ? 'linear-gradient(320deg, #000000 0%, #0A0A0A 100%)'
            : 'linear-gradient(320deg, #ff9a3520 1.52%, #00000000 90.82%)',
      }}
    >
      <Typography
        sx={{ mb: 2, fontWeight: 'normal', color: 'white' }}
        variant="h6"
      >
        Calls Triggered - in Last 7 Days
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <ResponsiveContainer height={screenHeight * 0.3} width="100%">
          <LineChart
            data={callsData}
            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid
              stroke={currentTheme === 'dark' ? '#444' : '#ccc'}
              strokeDasharray="3 3"
            />
            <XAxis dataKey="date" stroke="#aaa" />
            <YAxis tick={{ fill: '#aaa' }} />
            {/* @ts-expect-error */}
            <Tooltip content={(props) => <CustomTooltip {...props} />} />
            <Line
              activeDot={{
                r: 6,
                fill: currentTheme === 'dark' ? '#10B981' : '#333',
                stroke: '#fff',
                strokeWidth: 2,
              }}
              dataKey="calls"
              dot={{
                r: 5,
                stroke: '#10B981',
                strokeWidth: 2,
                fill: currentTheme === 'dark' ? '#1e1e1e' : '#fff',
              }}
              stroke="#10B981"
              strokeWidth={3}
              type="monotone"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

export default CallsChart;
